import {
  CheckCircle,
  ClockCounterClockwise,
  XCircle,
  ArrowUUpLeft,
  DeviceMobile,
} from '@phosphor-icons/react';
import { useState } from 'react';

import { Card } from '@shared/ui/Card';
import { haptics } from '@shared/lib/telegram';
import type { OrderDto, OrderStatus } from '../../api/orderApi';
import { itemToText } from '../../lib/itemToText';
import { ActivationPanel } from '../ActivationPanel/ActivationPanel';
import styles from './OrderCard.module.css';

const STATUS_ICON = {
  COMPLETED: CheckCircle,
  REFUNDED: ArrowUUpLeft,
  FAILED: XCircle,
  PENDING: ClockCounterClockwise,
  PAID: ClockCounterClockwise,
  AWAITING_SMS: DeviceMobile,
} as const satisfies Record<OrderStatus, unknown>;

const STATUS_LABEL: Record<OrderStatus, string> = {
  COMPLETED: 'Delivered',
  REFUNDED: 'Refunded',
  FAILED: 'Failed',
  PENDING: 'Processing',
  // Paid for, nothing handed over yet. On most products this lasts an instant,
  // but a product the shop prepares by hand sits here until someone sends it —
  // so it says Pending rather than pretending to be busy.
  PAID: 'Pending',
  // Not "pending": the buyer has something in hand — a live number — and the
  // wait is on the service they gave it to, not on us.
  AWAITING_SMS: 'Waiting for code',
};

export function OrderCard({ order }: { order: OrderDto }) {
  const [revealed, setRevealed] = useState(false);
  const Icon = STATUS_ICON[order.status];
  const items = order.deliveredItems ?? [];
  const hasItems = items.length > 0;

  const copy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      haptics.notify('success');
    } catch {
      haptics.notify('error');
    }
  };

  return (
    <Card className={styles.card}>
      <div className={styles.header}>
        <Icon size={20} weight="fill" className={styles[`status_${order.status}`]} />
        <div className={styles.headings}>
          <p className={styles.name}>{order.productName}</p>
          <p className={styles.meta}>
            {order.kind === 'GIVEAWAY' ? <span className={styles.giveawayBadge}>Giveaway</span> : null}
            {STATUS_LABEL[order.status]} · {new Date(order.createdAt).toLocaleDateString()}
          </p>
        </div>
        <p className={order.kind === 'GIVEAWAY' ? styles.freePrice : styles.price}>
          {order.kind === 'GIVEAWAY' ? 'FREE' : order.pricePaid.label}
        </p>
      </div>

      {/* The live number, its countdown and the release button. Rendered for
          settled number orders too, so the record of which number the code
          came to survives the order closing. */}
      {order.activation ? <ActivationPanel order={order} /> : null}

      {/* Waiting on a person, not a machine. Saying so stops the customer
          reading a paid order with nothing in it as a failed one. */}
      {order.status === 'PAID' && !hasItems ? (
        <p className={styles.awaiting}>
          {order.kind === 'GIVEAWAY'
            ? '🎁 You won! Your giveaway prize is being prepared and will arrive here and in your chat.'
            : '⏳ Being prepared for you — it will arrive here and in your chat shortly.'}
        </p>
      ) : null}

      {hasItems ? (
        <div className={styles.delivery}>
          {/* Credentials stay hidden until asked for: the app may be on screen in public. */}
          {revealed ? (
            <>
              {items.map((item, index) => {
                const text = itemToText(item);
                return (
                  <pre key={index} className={styles.item} onClick={() => void copy(text)}>
                    {text}
                  </pre>
                );
              })}
              <p className={styles.hint}>Tap an item to copy</p>

              {/* A redeem link on its own is not enough — most products need
                  steps, and some expire within a day of delivery. */}
              {order.instructions ? (
                <div className={styles.instructions}>
                  <p className={styles.instructionsTitle}>📖 How to use it</p>
                  <p className={styles.instructionsBody}>{order.instructions}</p>
                </div>
              ) : null}
            </>
          ) : (
            <button
              type="button"
              className={styles.reveal}
              onClick={() => {
                haptics.tap('light');
                setRevealed(true);
              }}
            >
              Show my item{items.length > 1 ? 's' : ''}
            </button>
          )}
        </div>
      ) : null}
    </Card>
  );
}
